import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Collapse,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import LockIcon from '@mui/icons-material/Lock';

/**
 * PermissionTreeNode component
 * Displays a single permission node with expand/collapse and action menu
 */
const PermissionTreeNode = ({
  permission,
  level = 0,
  onAddChild,
  onEdit,
  onView,
  onDelete,
  onMoveUp,
  onMoveDown
}) => {
  const [expanded, setExpanded] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const hasChildren = Array.isArray(permission.children) && permission.children.length > 0;
  const isParent = permission.is_parent || hasChildren;

  // Debug logging
  React.useEffect(() => {
    if (level === 0) {
      console.log('Root Permission:', {
        name: permission.name,
        path: permission.path,
        hasChildren,
        childrenCount: permission.children?.length || 0,
        isParent
      });
    }
  }, [permission, level, hasChildren, isParent]);

  // Handle expand/collapse
  const handleToggle = () => {
    if (hasChildren) {
      setExpanded(!expanded);
    }
  };

  // Handle menu open
  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Menu action handlers
  const handleAddChild = () => {
    handleMenuClose();
    onAddChild(permission);
  };

  const handleEdit = () => {
    handleMenuClose();
    onEdit(permission);
  };

  const handleView = () => {
    handleMenuClose();
    onView(permission);
  };

  const handleDelete = () => {
    handleMenuClose();
    onDelete(permission);
  };

  const handleMoveUp = () => {
    handleMenuClose();
    onMoveUp(permission);
  };

  const handleMoveDown = () => {
    handleMenuClose();
    onMoveDown(permission);
  };

  return (
    <Box>
      {/* Permission Node */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 1,
          px: 2,
          ml: level * 3,
          borderRadius: 1,
          '&:hover': {
            backgroundColor: 'action.hover',
            cursor: 'pointer'
          },
          transition: 'background-color 0.2s'
        }}
      >
        {/* Expand/Collapse Icon */}
        <IconButton
          size="small"
          onClick={handleToggle}
          sx={{
            mr: 1,
            visibility: hasChildren ? 'visible' : 'hidden'
          }}
        >
          {expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
        </IconButton>

        {/* Permission Icon */}
        <Box sx={{ mr: 2, color: isParent ? 'primary.main' : 'text.secondary' }}>
          {isParent ? <FolderIcon /> : <DescriptionIcon />}
        </Box>

        {/* Permission Info */}
        <Box sx={{ flex: 1, minWidth: 0 }} onClick={handleToggle}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle1" noWrap>
              {permission.name}
            </Typography>
            {permission.is_core && (
              <Tooltip title="Core permission - seeded from specification">
                <Chip
                  icon={<LockIcon />}
                  label="Core"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 20 }}
                />
              </Tooltip>
            )}
            {isParent && (
              <Chip
                label={`${permission.children_count || permission.children.length} children`}
                size="small"
                variant="outlined"
                sx={{ height: 20 }}
              />
            )}
            {permission.usage_count > 0 && (
              <Chip
                label={`Used in ${permission.usage_count} roles`}
                size="small"
                color="success"
                variant="outlined"
                sx={{ height: 20 }}
              />
            )}
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{ fontFamily: 'monospace' }}
          >
            {permission.path}
          </Typography>
          {permission.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {permission.description}
            </Typography>
          )}
        </Box>

        {/* Action Menu Button */}
        <IconButton
          size="small"
          onClick={handleMenuOpen}
          sx={{ ml: 1 }}
        >
          <MoreVertIcon />
        </IconButton>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleView}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>

          <MenuItem onClick={handleAddChild}>
            <ListItemIcon>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add Child Permission</ListItemText>
          </MenuItem>

          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>

          <MenuItem onClick={handleMoveUp}>
            <ListItemIcon>
              <ArrowUpwardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Move Up</ListItemText>
          </MenuItem>

          <MenuItem onClick={handleMoveDown}>
            <ListItemIcon>
              <ArrowDownwardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Move Down</ListItemText>
          </MenuItem>

          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {/* Children (Recursive) */}
      {hasChildren && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box>
            {permission.children.map((child) => (
              <PermissionTreeNode
                key={child.id}
                permission={child}
                level={level + 1}
                onAddChild={onAddChild}
                onEdit={onEdit}
                onView={onView}
                onDelete={onDelete}
                onMoveUp={onMoveUp}
                onMoveDown={onMoveDown}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

/**
 * PermissionTreeView component
 * Displays the entire permission tree with all nodes
 */
const PermissionTreeView = ({
  permissions,
  onAddChild,
  onEdit,
  onView,
  onDelete,
  onMoveUp,
  onMoveDown
}) => {
  return (
    <Box>
      {permissions.map((permission) => (
        <PermissionTreeNode
          key={permission.id}
          permission={permission}
          level={0}
          onAddChild={onAddChild}
          onEdit={onEdit}
          onView={onView}
          onDelete={onDelete}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
        />
      ))}
    </Box>
  );
};

export default PermissionTreeView;
